import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PawPrint, Heart } from "@phosphor-icons/react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const HERO = "https://static.prod-images.emergentagent.com/jobs/062e260e-b0f9-432f-a759-5890d554bb91/images/77dcba3d2c3d93b2f7d3295a5788d51720e390eea2bd1f117ea8d199e9285e68.jpeg";
const SLOGANS = ["KILLED TOO YOUNG", "WHERE WERE YOU?", "SAY THEIR NAMES", "ADOPT DON'T SHOP", "BRING THEM HOME", "UNBREAKABLE"];

export default function MissionDrop() {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products").then(({ data }) => setAll(data)).finally(() => setLoading(false));
  }, []);

  const mission = useMemo(() => all.filter((p) => (p.tags || []).includes("mission")), [all]);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="mission-drop-page">
      {/* Cinematic hero */}
      <div className="relative h-screen min-h-[560px] w-full overflow-hidden">
        <img src={HERO} alt="The Mission Drop" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/65" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-center">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="text-sm sm:text-base font-bold uppercase tracking-[0.3em] mb-4" style={{ color: "#ccff00" }}>
            The Mission Drop
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }}
            className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl sm:text-7xl lg:text-8xl max-w-4xl">
            They were <span style={{ color: "#ccff00" }}>killed too young.</span> Where were you?
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.14 }}
            className="mt-6 max-w-xl text-muted-foreground text-base">
            Every piece funds the ranch — 300 acres, 3,000 pits pulled off the list. Wear the anger. Fund the answer.
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="mt-8 flex flex-wrap gap-4">
            <a href="#drop" className="inline-flex items-center gap-2 px-6 py-3.5 font-black uppercase tracking-wide text-black hover:opacity-90 transition-opacity" style={{ background: "#ccff00" }} data-testid="mission-shop-btn">
              <PawPrint weight="fill" size={18} /> Shop the drop
            </a>
            <Link to="/donate" className="inline-flex items-center gap-2 px-6 py-3.5 font-black uppercase tracking-wide border text-[#ccff00] hover:bg-[#ccff00] hover:text-black transition-colors" style={{ borderColor: "#ccff00" }} data-testid="mission-donate-btn">
              <Heart weight="fill" size={18} /> Fund the ranch
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Kinetic marquee */}
      <div className="overflow-hidden py-3" style={{ background: "#ccff00" }} data-testid="mission-marquee">
        <div className="marquee-track font-display font-black uppercase text-2xl tracking-tighter text-black">
          {[...SLOGANS, ...SLOGANS].map((s, i) => (
            <span key={i} className="px-6">{s} <span className="opacity-40">✦</span></span>
          ))}
        </div>
      </div>

      {/* The drop */}
      <div id="drop" className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <h2 className="font-display font-black uppercase text-4xl sm:text-6xl tracking-tighter mb-3">Say their <span style={{ color: "#ccff00" }}>names.</span></h2>
        <p className="text-muted-foreground mb-10 max-w-xl">Smiley. Beats. King. And the thousands with no name at all. This is for them.</p>
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[4/5] rounded-xl bg-zinc-900 animate-pulse" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="mission-grid">
            {mission.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
